import zionDebug from '@fs/zion-debug'
import axios from '@fs/zion-axios'
import { buildLangUrl } from '@fs/zion-router'
import { getMostRecentFieldValue } from '@fs/zion-sls'
import { getImageMetaData } from '@fs/zion-image-viewer'
import { getImagePermissions } from '../../../shared/services/platform/platformService'
import { getArtifact } from '../../../shared/services/rms/rmsService'
import { errorCollector } from '../../../shared/utils/utils'
import { normalizeForMatching } from '../../../shared/utils/stringNormalization'

const debug = zionDebug('rip:FullText.utils')

/**
 * Builds the request body for an AI summary. The server builds the prompt from these fields; the client must never
 * send a system message or a messages array.
 * @param {string} plainTextTranscript - The raw text to summarize
 * @param {Object} [recordContext] - Known record fields: place, recordType, recordDate, language, imageClassification
 * @param {string} [outputLanguage] - UI language to write the summary in (names and places stay in their original language)
 * @returns {Object} The request body
 */
export function buildSummaryRequestBody(plainTextTranscript, recordContext, outputLanguage) {
  return {
    task: 'summary',
    transcript: plainTextTranscript,
    recordContext: recordContext || {},
    outputLanguage,
  }
}

/**
 * Requests an AI summary without streaming.
 * Errors are not caught here, so the caller can show the error state and report it. If this returned null on error,
 * the caller would cache an empty summary for the image.
 */
export async function getTextTranscript(url, plainTextTranscript, recordContext, outputLanguage) {
  const response = await axios.post(
    buildLangUrl(url),
    buildSummaryRequestBody(plainTextTranscript, recordContext, outputLanguage),
    { headers: { Accept: 'text/plain' } }
  )
  return response.data
}

/**
 * Streams text transcript processing with real-time chunk delivery.
 *
 * This function intentionally uses await in a loop for streaming data processing.
 * Each chunk must be fully processed before proceeding to maintain proper streaming
 * behavior and ensure data integrity. The sequential processing is required for
 * the streaming response to work correctly with the onChunk callback.
 *
 * @param {string} url - The API endpoint URL for text processing
 * @param {string} plainTextTranscript - The raw text to be processed
 * @param {function} onChunk - Callback function called for each streamed chunk
 * @param {Object} [recordContext] - Optional known record fields the server uses to prime the prompt
 * @param {string} [outputLanguage] - Optional UI language to write the summary in (names and places stay in their original language)
 * @returns {Promise<string>} The complete processed response
 */
export async function getTextTranscriptStreaming(url, plainTextTranscript, onChunk, recordContext, outputLanguage) {
  try {
    const response = await fetch(buildLangUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(buildSummaryRequestBody(plainTextTranscript, recordContext, outputLanguage)),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`)
    }

    if (!response.body) {
      throw new Error('Response body is null or undefined')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let fullResponse = ''

    try {
      let done = false
      while (!done) {
        // The use of 'await' in this loop is intentional and necessary for streaming.
        // We must wait for each chunk from the stream before proceeding to the next
        // iteration.
        // eslint-disable-next-line no-await-in-loop
        const result = await reader.read()
        done = result.done
        const value = result.value

        if (value) {
          const chunkText = decoder.decode(value, { stream: true })
          fullResponse += chunkText
          if (onChunk) {
            onChunk(chunkText, fullResponse)
          }
        }
      }

      return fullResponse
    } finally {
      reader.releaseLock()
    }
  } catch (error) {
    errorCollector(error, 'Error in streaming:')
    throw error
  }
}

/**
 * Matches a phrase by comparing normalized word sequences.
 * Extracts words from each field after normalization, skipping fields that
 * normalize to empty strings (e.g., special character fields like "↔").
 *
 * @param {string} keyword - The search phrase (e.g., "francisco y dolores maria")
 * @param {Array} fs - Array of field objects to check
 * @returns {boolean} - True if the normalized word sequence matches
 */
export const matchPhrase = (keyword, fs) => {
  // Extract normalized words from the keyword
  const keywordWords = normalizeForMatching(keyword)
    .split(/\s+/)
    .filter((w) => w.length > 0)

  // Extract normalized words from fields, skipping empty normalizations
  const fieldWords = []
  fs.forEach((f) => {
    const fieldValue = getMostRecentFieldValue(f)
    const normalized = normalizeForMatching(fieldValue)
    // Split and collect non-empty words
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.push(...words)
  })

  // Compare word sequences
  if (keywordWords.length !== fieldWords.length) return false
  return keywordWords.every((word, i) => word === fieldWords[i])
}

/**
 * Builds a list of fields that match search keywords, handling both single
 * keywords and multi-word phrases. Normalizes text to handle diacritics and
 * special characters, skipping fields that normalize to empty strings.
 *
 * @param {Array<string>} keywords - Array of search terms (can be single words or phrases)
 * @param {Array} fields - Array of field objects from the transcript
 * @returns {Array} - Array of matching fields marked with isKeyword: true
 */
export function buildKeywordFields(keywords, fields) {
  const keywordFieldS = []
  const seenFields = new Set() // Use Set for O(1) duplicate checking
  if (!fields) return keywordFieldS

  // PERFORMANCE OPTIMIZATION: Precompute normalized values once per field
  // Previously normalized each field O(n) times in phrase matching loops
  // This reduces buildKeywordFields from O(n²) to O(n) complexity
  const fieldNormalizations = new Map() // field → normalized string
  const fieldWords = new Map() // field → normalized word array

  fields.forEach((field) => {
    const fieldValue = getMostRecentFieldValue(field)
    const normalized = normalizeForMatching(fieldValue)

    // Store full normalized string for single keyword matching
    fieldNormalizations.set(field, normalized)

    // Store word array for phrase matching
    const words = normalized.split(/\s+/).filter((w) => w.length > 0)
    fieldWords.set(field, words)
  })
  if (keywords)
    keywords.forEach((keyword) => {
      const words = keyword?.split(' ')
      if (words?.length > 1) {
        // Multi-word phrase matching
        // Extract normalized words from keyword
        const keywordWords = normalizeForMatching(keyword)
          .split(/\s+/)
          .filter((w) => w.length > 0)

        // Try starting at each field position
        for (let startIndex = 0; startIndex < fields.length; startIndex++) {
          const collectedWords = []
          const collectedFields = []
          let currentIndex = startIndex

          // Collect fields until we have enough normalized words
          while (collectedWords.length < keywordWords.length && currentIndex < fields.length) {
            const field = fields[currentIndex]

            // OPTIMIZATION: Use precomputed words (O(1)) instead of re-normalizing (O(m))
            const normalizedWords = fieldWords.get(field)

            // Always collect the field, even if it normalizes to empty (e.g., "↔")
            collectedFields.push(field)
            // But only collect its words if it has any
            collectedWords.push(...normalizedWords)
            currentIndex++
          }

          // Check if collected words match keyword words
          if (
            collectedWords.length === keywordWords.length &&
            collectedWords.every((word, i) => word === keywordWords[i])
          ) {
            // Mark all collected fields (including special char fields)
            collectedFields.forEach((field) => {
              if (!seenFields.has(field)) {
                seenFields.add(field)
                keywordFieldS.push(field)
              }
            })
          }
        }
      } else {
        // Single keyword matching using precomputed normalization
        const normalizedKeyword = normalizeForMatching(keyword)

        fields.forEach((f) => {
          // OPTIMIZATION: Use precomputed value (O(1)) instead of re-normalizing
          const normalizedValue = fieldNormalizations.get(f)

          if (normalizedKeyword === normalizedValue) {
            if (!seenFields.has(f)) {
              seenFields.add(f)
              keywordFieldS.push(f)
            }
          }
        })
      }
    })
  keywordFieldS.forEach((kf) => {
    kf.isKeyword = true
  })
  return keywordFieldS
}

// The one AI route. The server picks the model, so the route name and this code don't name or choose the model.
const AI_ENDPOINT = '/records/improve/ai'

/**
 * Determines if streaming mode should be used: the route always streams, so stream when the caller can show it.
 * @param {Function} setStreamingContent - Callback to set streaming content
 * @param {Function} setIsStreaming - Callback to set streaming state
 * @returns {boolean} Whether streaming should be used
 */
function shouldUseStreaming(setStreamingContent, setIsStreaming) {
  return Boolean(setStreamingContent && setIsStreaming)
}

/**
 * Executes a streaming AI request with proper state management.
 *
 * @param {string} endpoint - The API endpoint URL
 * @param {string} plainTextTranscript - The text to process
 * @param {Function} setStreamingContent - Callback to set streaming content
 * @param {Function} setIsStreaming - Callback to set streaming state
 * @param {Function} setLoading - Callback to set loading state
 * @param {Function} isStale - Returns true if the request's image is no longer the one on screen
 * @param {Object} [recordContext] - Optional known record fields the server uses to prime the prompt
 * @param {string} [outputLanguage] - Optional UI language to write the summary in (names and places stay in their original language)
 * @returns {Promise<string>} The complete response text
 */
async function executeStreamingRequest(
  endpoint,
  plainTextTranscript,
  setStreamingContent,
  setIsStreaming,
  setLoading,
  isStale,
  recordContext,
  outputLanguage
) {
  setIsStreaming(true)
  setLoading(false) // Turn off loading immediately so skeleton can show

  const response = await getTextTranscriptStreaming(
    endpoint,
    plainTextTranscript,
    (chunk, fullResponse) => {
      // Drop chunks for a request whose image the user has since navigated away from,
      // so a stale stream can't paint over the summary for the image now on screen.
      if (!isStale()) setStreamingContent(fullResponse)
    },
    recordContext,
    outputLanguage
  )

  if (!isStale()) {
    setIsStreaming(false)
    setStreamingContent('') // Clear streaming content once complete
  }

  return response
}

/**
 * Builds the known record fields that the server uses to prime the summary prompt. Per-image metadata comes first,
 * then the artifact's coverage and language.
 * @returns {Object} The record context: place, recordType, recordDate, language, imageClassification
 */
function buildSummaryRecordContext({
  coverages,
  recordPlace,
  recordDate,
  recordLanguage,
  artifactLanguage,
  imageClassification,
}) {
  const coverage = coverages?.[0]
  return {
    place: recordPlace || coverage?.place || coverage?.placeOrig,
    recordType: coverage?.recordTypeOrig,
    recordDate,
    language: recordLanguage || artifactLanguage,
    imageClassification,
  }
}

/**
 * Sends the summary request: streamed when the caller can show streaming content, otherwise in one response.
 * @returns {Promise<string|Object>} The response text, or the response data for a non-streaming request
 */
function requestSummary({
  plainTextTranscript,
  recordContext,
  outputLanguage,
  setStreamingContent,
  setIsStreaming,
  setLoading,
  isStale,
}) {
  if (shouldUseStreaming(setStreamingContent, setIsStreaming)) {
    return executeStreamingRequest(
      AI_ENDPOINT,
      plainTextTranscript,
      setStreamingContent,
      setIsStreaming,
      setLoading,
      isStale,
      recordContext,
      outputLanguage
    )
  }
  return getTextTranscript(AI_ENDPOINT, plainTextTranscript, recordContext, outputLanguage)
}

/**
 * Returns the summary text from a response. An empty answer means the request failed (for example, the provider failed
 * after the stream started), so this throws, and the caller shows the error and doesn't cache it.
 * @param {string|Object} response - The response text, or response data with a result field
 * @returns {string} The summary text
 * @throws {Error} If the answer is empty
 */
function getSummaryResponseText(response) {
  const responseText = response?.result || response
  if (typeof responseText !== 'string' || !responseText.trim()) {
    throw new Error('AI summary response was empty')
  }
  return responseText
}

/**
 * While the transcript is not ready, the summary waits. If the transcript failed to load, it will never be ready, so
 * show the error instead of loading forever.
 */
function showErrorIfTranscriptFailed({ transcriptError, setLoading, setSummaryError }) {
  if (transcriptError) {
    setLoading(false)
    setSummaryError(true)
  }
}

/**
 * Shows the summary error state and clears any streaming state.
 */
function showSummaryError({ setIsStreaming, setStreamingContent, setSummaryError }) {
  if (setIsStreaming) setIsStreaming(false)
  if (setStreamingContent) setStreamingContent('')
  setSummaryError(true)
}

export const fetchSummarization = async ({
  currentImageArk,
  currentImageArkRef,
  plainTextTranscript,
  isTranscriptReady = true,
  transcriptError = false,
  cachedSummarizations,
  setSummarizationData,
  setCachedSummarizations,
  setLoading,
  setSummaryError,
  parseTranscript,
  hasMeaningfulContent,
  setStreamingContent,
  setIsStreaming,
  coverages,
  artifactLanguage,
  recordPlace,
  recordDate,
  imageClassification,
  recordLanguage,
  outputLanguage,
}) => {
  // True once the user has navigated to a different image while this request was in flight.
  const isStale = () => Boolean(currentImageArkRef) && currentImageArkRef.current !== currentImageArk
  try {
    // Early returns for missing data
    if (!plainTextTranscript || !currentImageArk) {
      setSummarizationData(null)
      setLoading(false)
      return
    }

    // Check cache
    if (cachedSummarizations[currentImageArk]) {
      setSummarizationData(cachedSummarizations[currentImageArk])
      setLoading(false)
      return
    }

    setLoading(true)

    // While the new image's transcript loads, plainTextTranscript can still be the previous image's text. Wait, so a
    // summary of the wrong transcript is not cached under this image. The caller runs this again when it is ready.
    if (!isTranscriptReady) {
      showErrorIfTranscriptFailed({ transcriptError, setLoading, setSummaryError })
      return
    }

    // Validate content
    if (!hasMeaningfulContent(plainTextTranscript)) {
      setSummarizationData(null)
      setLoading(false)
      return
    }

    const recordContext = buildSummaryRecordContext({
      coverages,
      recordPlace,
      recordDate,
      recordLanguage,
      artifactLanguage,
      imageClassification,
    })
    debug('Record context for AI prompt:', { recordContext, outputLanguage: outputLanguage || '(none)' })

    const response = await requestSummary({
      plainTextTranscript,
      recordContext,
      outputLanguage,
      setStreamingContent,
      setIsStreaming,
      setLoading,
      isStale,
    })
    const responseText = getSummaryResponseText(response)

    // Parse and cache response. The cache is keyed by the request's own image ark, so it's
    // still safe to store even if the user has since navigated to a different image.
    const parsedData = parseTranscript(responseText)
    setCachedSummarizations((prevTranscripts) => ({
      ...prevTranscripts,
      [currentImageArk]: parsedData,
    }))
    if (isStale()) return
    setSummarizationData(parsedData)
    setSummaryError(false)
  } catch (error) {
    if (isStale()) return
    showSummaryError({ setIsStreaming, setStreamingContent, setSummaryError })
    errorCollector(error, currentImageArk, {
      codeId: 29,
    })
  } finally {
    // Only set loading to false if not in streaming mode (streaming mode already set it to false)
    const wasStreaming = shouldUseStreaming(setStreamingContent, setIsStreaming)
    if (!wasStreaming && !isStale()) {
      setLoading(false)
    }
  }
}

export const getImagePermissionsBoolean = async (partitionKey, dispatch) => {
  if (partitionKey !== undefined) {
    const data = await getImagePermissions(null, partitionKey)
    dispatch({
      type: 'SET_IMAGE_PERMISSION',
      value: data,
    })
  }
}

export const getDownloadPermission = async (imageArk, dispatch) => {
  if (!imageArk) return

  try {
    const metadata = await getImageMetaData(imageArk)
    // Use the same download property that ComboViewer uses
    const downloadAllowed = metadata?.download ?? false

    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: downloadAllowed,
    })
  } catch (error) {
    console.error('[FullText] Failed to fetch download permission:', error)
    // Default to restricted on error (fail-safe)
    dispatch({
      type: 'SET_DOWNLOAD_PERMISSION',
      value: false,
    })
  }
}

export const fetchArtifact = async (thumbnails, currentImageArk, setArtifact, setArtifactLoadErr) => {
  const apid = thumbnails?.find((thumb) => thumb.ark === currentImageArk)?.id
  if (apid) {
    try {
      const artifactData = await getArtifact(apid)
      setArtifact(artifactData)
    } catch (err) {
      errorCollector(err, currentImageArk, {
        codeId: 25,
      })
      setArtifactLoadErr(true)
    }
  }
}
